import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { db } from '../Components/auth/firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  Box, FormControl, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Dialog, DialogActions, InputLabel, DialogContent, DialogTitle, TextField, Button, Select, MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../Components/context/SearchContext'; // Import context for search
import SideDrawer from '../Components/newcomponents/SideDrawer';
import {
  updateUserGroups,
  fetchUserGroupsByTenant,
  fetchTenantUsers,
  deleteUser,
  addUser
} from "../DatabaseApis/DBQuery";
import { getCookie } from '../utils/cookieUtils';
// State for tenant data

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '200px',
}));

const UserList = () => {
  const [currentTenantId, setCurrentTenantId] = useState('');
  const [users, setUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [newAssignedGroups, setNewAssignedGroups] = useState({});
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);  // State for the dialog
  const [newUser, setNewUser] = useState({ userName: '', email: '', tenantId: '', assignedGroups: [], role: '' }); // New user state
  const navigate = useNavigate();
  const [userIdToDelete, setUserIdToDelete] = useState(null); // Store user ID to delete
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const { searchTerm } = useSearch(); // Get search term from context
  useEffect(() => {
    const resolveTenantId = async () => {
      try {
        // Prefer tenant chosen at login (stored in cookie) for multi-tenant users.
        const sessionUserData = getCookie('userData');
        if (sessionUserData?.tenantId) {
          setCurrentTenantId(sessionUserData.tenantId);
          return;
        }

        // Fallback: derive from users collection by current auth email.
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.email) {
          const usersQ = query(collection(db, 'users'), where('email', '==', currentUser.email.toLowerCase()));
          const snapshot = await getDocs(usersQ);
          const userDocData = snapshot.docs[0]?.data();
          if (userDocData?.tenantId) {
            setCurrentTenantId(userDocData.tenantId);
          }
        }
      } catch (error) {
        console.error('Error resolving tenant for user list:', error);
      }
    };

    resolveTenantId();
  }, []);

  useEffect(() => {
    if (!currentTenantId) return;

    let isMounted = true;

    const loadTenantScopedData = async () => {
      try {
        const [tenantUsers, groups] = await Promise.all([
          fetchTenantUsers(currentTenantId),
          fetchUserGroupsByTenant(currentTenantId),
        ]);
        if (!isMounted) return;
        setUsers(tenantUsers);
        setUserGroups(groups);
      } catch (error) {
        console.error('Error fetching tenant-scoped users/groups:', error);
      }
    };

    loadTenantScopedData();
    return () => {
      isMounted = false;
    };
  }, [currentTenantId]);

  useEffect(() => {
    // Filter users based on the search term and currentTenantId from Firestore
    setFilteredUsers(
      users.filter(user =>
        user.tenantId === currentTenantId && (
          user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.id.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    );
  }, [searchTerm, users, currentTenantId]); // Re-run when searchTerm/users/tenant changes

  const handleEditClick = (user) => {
    setEditingUser(user.id);
    setNewAssignedGroups({ [user.id]: user.assignedGroups || [] });
  };

  const handleSaveClick = async (userId) => {
    try {
      const updatedGroups = newAssignedGroups[userId] || [];
      await updateUserGroups(userId, updatedGroups);
      
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, assignedGroups: updatedGroups } : user
        )
      );
  
      setEditingUser(null);
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleCancelClick = () => {
    setEditingUser(null);
  };

  const handleDropdownChange = (event, userId) => {
    setNewAssignedGroups({
      ...newAssignedGroups,
      [userId]: Array.from(event.target.selectedOptions, option => option.value),
    });
  };
  const handleDeleteClick = (id) => {
    setUserIdToDelete(id);
    setOpenConfirmDelete(true); // Show confirmation dialog
  };

  // const handleDelete = async () => {
  //   try {
  //     await deleteDoc(doc(db, 'users', userIdToDelete));
  //     setUsers(users.filter(user => user.id !== userIdToDelete));
  //   } catch (error) {
  //     console.error('Error deleting user:', error);
  //   } finally {
  //     setOpenConfirmDelete(false); // Close the confirmation dialog
  //     setUserIdToDelete(null); // Reset user ID
  //   }
  // };

  const handleDelete = async () => {
    try {
      await deleteUser(userIdToDelete); // Use stubbed function
      setUsers(users.filter(user => user.id !== userIdToDelete)); // Remove from state
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setOpenConfirmDelete(false); // Close dialog
      setUserIdToDelete(null); // Reset
    }
  };

  const getGroupNameById = (groupId) => {
    const group = userGroups.find(group => group.id === groupId);
    return group ? group.groupName : 'Unknown';
  };

  const handleAddClick = () => {
    // Auto-set the new user's tenantId to the current user's tenant and clear other fields
    setNewUser({ userName: '', email: '', tenantId: currentTenantId, assignedGroups: [], role: '' });
    setOpenDialog(true);  // Open the dialog
  };

  const handleDialogClose = () => {
    setOpenDialog(false);  // Close the dialog
    // Reset the newUser state to clear form fields
    setNewUser({ userName: '', email: '', tenantId: '', assignedGroups: [], role: '' });
  };




  const handleDialogSave = async () => {
    try {
      // Add new user to Firestore
      // const userRef = await addDoc(collection(db, 'users'), newUser);
      // const userId = userRef.id; // Get the document ID (which will be used as userId)

      // // Update the newly created document to include the userId field
      // await updateDoc(doc(db, 'users', userId), { userId });

      const userId = await addUser(newUser);

      // Refresh only current tenant users instead of full collection fetch.
      const refreshedUsers = await fetchTenantUsers(currentTenantId);
      setUsers(refreshedUsers);

      // Close dialog after saving and fetching
      handleDialogClose();
    } catch (error) {
      console.error('Error adding user:', error);
    }
  };


  const handleNewUserChange = (event) => {
    const { name, value } = event.target;
    
    // If tenant ID changes, clear assigned groups to prevent cross-tenant data leaks
    if (name === 'tenantId') {
      setNewUser({ ...newUser, [name]: value, assignedGroups: [] });
    } else {
      setNewUser({ ...newUser, [name]: value });
    }
  };

  return (
    <Box sx={{ marginLeft: '70px' }}>
      <Typography variant="h4" gutterBottom >
        User List
      </Typography>
      <SideDrawer/>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>User Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Assigned Groups</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.userName}</TableCell>
                <TableCell>{user.role || 'User'}</TableCell>
                <TableCell>
                  {editingUser === user.id ? (
                    <StyledTextField
                      select
                      multiple
                      value={newAssignedGroups[user.id] || []}
                      onChange={(e) => handleDropdownChange(e, user.id)}
                      SelectProps={{
                        multiple: true,
                        native: true,
                      }}
                      variant="outlined"
                    >
                      <option value="" disabled>Select Groups</option>
                      {userGroups
                        .filter(group => group.tenantId === user.tenantId)
                        .map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.groupName}
                          </option>
                        ))}
                    </StyledTextField>
                  ) : (
                    (user.assignedGroups || []).map(groupId => getGroupNameById(groupId)).join(', ') || 'None'
                  )}
                </TableCell>
                <TableCell>
                  {editingUser === user.id ? (
                    <>
                      <IconButton color="primary" aria-label="save" onClick={() => handleSaveClick(user.id)}>
                        <SaveIcon />
                      </IconButton>
                      <IconButton color="secondary" aria-label="cancel" onClick={handleCancelClick}>
                        <CancelIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton color="primary" aria-label="edit" onClick={() => handleEditClick(user)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        aria-label="delete"
                        onClick={() => handleDeleteClick(user.id)} // Change to handleDeleteClick
                        style={{ color: 'red' }} // Change icon color to red
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Add User Button */}
      <IconButton
        color="primary"
        onClick={handleAddClick}
        sx={{ position: 'fixed', bottom: 16, right: 16, backgroundColor: 'primary.main', color: 'white' }}
      >
        <AddIcon />
      </IconButton>
      {/* Confirmation Dialog for Delete */}
      <Dialog
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDelete(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog for adding a new user */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="userName"
            label="User Name"
            type="text"
            fullWidth
            value={newUser.userName}
            onChange={handleNewUserChange}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            value={newUser.email}
            onChange={handleNewUserChange}
          />
          {/* Tenant ID is auto-selected to the current user's tenant; field removed from UI */}
    
          <FormControl fullWidth margin="dense">
            <InputLabel id="assigned-groups-label">Assigned Groups</InputLabel>
            <Select
              margin='dense'
              labelId="assigned-groups-label"
              label="Assigned Groups"
              fullWidth
              name="assignedGroups"
              multiple
              value={newUser.assignedGroups}
              onChange={handleNewUserChange}
            >
              {userGroups
                .filter(group => group.tenantId === newUser.tenantId)
                .map(group => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.groupName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
         
          <FormControl fullWidth margin="dense">
            <InputLabel id="role">Role</InputLabel>
            <Select
              labelId='role'
              label="Role"
              fullWidth
              name="role"
              value={newUser.role}
              onChange={handleNewUserChange}
              margin='dense'
            >
              {/* <MenuItem value="Super Admin">Super Admin</MenuItem> */}
              <MenuItem value="Admin">Admin</MenuItem>
              {/* <MenuItem value="Operator">Operator</MenuItem> */}
              <MenuItem value="User">User</MenuItem>
              {/* <MenuItem value="Guest">Guest</MenuItem> */}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
         
          <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
                <Button variant="contained"
                  color="primary" onClick={handleDialogSave} >
                  Save
                </Button>
                <Button onClick={handleDialogClose} >
                  Cancel
                </Button>
              </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;
